import i18n, { type TranslationType } from '@atpoint/atpjs/helpers/i18n'
import type { ValueType } from '@atpoint/atpjs/validation'
import { ands, predicate, withMessage } from '@atpoint/atpjs/validation/functions'
import { type ValidatorType, validate, validateObject } from '@atpoint/atpjs/validation/validationsLib'
import {
  email,
  equals,
  nonEmptyString,
  number,
  numeric,
  optional,
  phone,
  validDate,
  validTime,
} from '@atpoint/atpjs/validation/validators'
import { List, fromJS } from 'immutable'
import type { Map as ImmutableMap } from 'immutable'
import { swissIban } from 'swisscard/helpers/custom_iban_validator'

const t = i18n.prefix(['errors'])

// NOTE: consult https://atpoint.atlassian.net/browse/SCMF-150
// bin-range - Bank Identification Number - refers to a set of codes
// 3776|4731|5100 for Amex, Visa and Mastercard respectively. These codes
// should not be present in string_input and text_input from the Forms.
//
// Input examples can have 15 or 16 numeric chars of length.
const BIN_NUMBER_REGEX = /\b(3776|4731|5100)\d{4}\d{4}\d{3,4}|\b(3776|4731|5100)\s*\d{4}\s*\d{4}\s*\d{3,4}/

export const nonBinRange = (errorMessage: TranslationType | string | null = null) =>
  withMessage(
    predicate((v: ValueType) => typeof v === 'string' && !BIN_NUMBER_REGEX.test(v)),
    errorMessage || 'value cannot include a bin-range',
  )

export const validateEntry = (element: ImmutableMap<string, any>) => {
  const value = element.get('value') as string
  const type = element.get('type') as string
  const optional = element.get('required') !== true
  return validateByType(type, value, optional)
}

export const array = (errorMessage: TranslationType | string | null = null) =>
  withMessage(
    predicate((v: any) => (v || List()).size > 0),
    errorMessage || 'no entries in list',
  )

export const validFile = (errorMessage: string | null = null) =>
  withMessage(
    predicate((v) => v instanceof Blob),
    errorMessage || 'no file selected',
  )

const computeOptionality = (validation: ValidatorType, isOptional: boolean) => {
  return isOptional ? optional(validation) : validation
}

const computeAddressValidation = (isOptional: boolean) => {
  return {
    street: computeOptionality(nonEmptyString(t(['default'])), isOptional),
    zip: computeOptionality(numeric(t(['default'])), isOptional),
    city: computeOptionality(nonEmptyString(t(['default'])), isOptional),
  }
}

const computeNameValidation = (isOptional: boolean) => {
  return {
    first_name: computeOptionality(nonEmptyString(t(['default'])), isOptional),
    last_name: computeOptionality(nonEmptyString(t(['default'])), isOptional),
  }
}

const computeBusinessPlaceInputAddressValidation = (isOptional: boolean) => {
  return {
    street1: computeOptionality(nonEmptyString(t(['default'])), isOptional),
    street2: optional(nonEmptyString(t(['default']))),
    zip: computeOptionality(numeric(t(['default'])), isOptional),
    city: computeOptionality(nonEmptyString(t(['default'])), isOptional),
  }
}

const validateByType = (type: string, value: any, isOptional: boolean) => {
  const defaultValidation = nonEmptyString(t(['default']))
  const textValidation = ands([defaultValidation, nonBinRange(t(['inputContainsBinRange']))])

  const handler =
    {
      string_input: () => validate(computeOptionality(textValidation, isOptional), value),
      text_input: () => validate(computeOptionality(textValidation, isOptional), value),
      currency_input: () => validate(computeOptionality(number(t(['currency'])), isOptional), value),
      date_input: () => validate(computeOptionality(validDate(t(['date'])), isOptional), value),
      time_input: () => validate(computeOptionality(validTime(t(['time'])), isOptional), value),
      email_input: () => validate(computeOptionality(email(t(['email'])), isOptional), value),
      phone_input: () => validate(computeOptionality(phone(t(['phone'])), isOptional), value),
      iban_input: () => validate(computeOptionality(swissIban(t(['iban'])), isOptional), value),
      checkboxes_input: () => validate(computeOptionality(array(t(['default'])), isOptional), value),
      acceptance_input: () => validate(computeOptionality(equals(true, t(['acceptance'])), isOptional), value),
      address_input: () => validateObject(computeAddressValidation(isOptional), value),
      names_input: () => validateObject(computeNameValidation(isOptional), value),
      file_input: () => validate(computeOptionality(validFile(t(['file'])), isOptional), value),
      business_place_input: () => {
        return validateObject(
          computeBusinessPlaceInputAddressValidation(isOptional),
          value ? value.get('address') : null,
        )
      },
    }[type] || (() => validate(computeOptionality(defaultValidation, isOptional), value))
  return fromJS(handler())
}
